import { ComponentType, memo, ReactElement, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { UserPopulatedDTO } from './api/generated';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { DocumentationPage } from './components/Documentation/DocumentationPage';
import { RecordingsPage } from './components/Recordings/RecordingsPage';
import { SessionInfopoint } from './components/SelfService/Routes/SessionInfopoint';
import { SessionRequest } from './components/SelfService/Routes/SessionRequest';
import { HeapPage } from './pages/HeapLiveView';
import { HeapRecordingsPage } from './pages/HeapRecordingsPage';
import { JobsiteSelect } from './pages/JobsiteSelect';
import { NoPage } from './pages/NoPage';
import { SelfServicePage } from './pages/SelfServicePage';
import { UserAdminPage } from './pages/UserAdminPage';
import { UserProvider, useUserContext } from './UserContext';
import LiveViewPage from './pages/LiveViewPage';
import BillableMaterialPage from './pages/BillableMaterialPage';
import SessionErrorScreen from './components/SelfService/SessionErrorScreen';
import { clearPendingJobsiteRedirect, isDocumentationEnabled, isLiveEnabled } from './utils/navigationUtils';
import BeltObservations from './components/Belt/BeltObservations';

interface AuthGuardedProps {
  Component: ComponentType<{ user: UserPopulatedDTO }>;
  requireJobsite?: boolean;
}

const AuthGuarded = memo(function ({ Component, requireJobsite = false }: AuthGuardedProps): ReactElement {
  const { data: user } = useUserContext();

  if (user) {
    if (!requireJobsite || user.preferredJobsite) {
      return <Component user={user} />;
    } else {
      return <AuthGuarded Component={JobsiteSelect} />;
    }
  } else {
    return <></>;
  }
});

const AuthenticatedRoutes = memo(function (): ReactElement {
  return (
    <AuthProvider>
      <RequireAuth>
        <UserProvider>
          <AppRoutes />
        </UserProvider>
      </RequireAuth>
    </AuthProvider>
  );
});

const AppRoutes = memo(function (): ReactElement {
  const { data: user, isLoading: userLoading } = useUserContext();
  const location = useLocation();

  // Track the last visited route globally (exclude specific routes)
  useEffect(() => {
    if (location.pathname === '/jobsite-select') {
      return;
    }

    clearPendingJobsiteRedirect();

    const excludedRoutes = ['/'];
    if (!excludedRoutes.includes(location.pathname)) {
      localStorage.setItem('lastVisitedRoute', location.pathname);
    }
  }, [location.pathname]);

  if (userLoading || !user) {
    return <></>;
  }

  const jobsiteType = user?.preferredJobsite?.type;

  switch (jobsiteType) {
    case 'train':
      return (
        <Routes key='train-routes'>
          <Route path='/' element={<Navigate to='reports' />} />
          <Route path='/documentation' element={<Navigate to='/reports' />} />
          <Route path='reports' element={<AuthGuarded requireJobsite={true} Component={RecordingsPage} />} />
          <Route path='jobsite-select' element={<AuthGuarded Component={JobsiteSelect} />} />
          <Route path='user-admin' element={<AuthGuarded Component={UserAdminPage} />} />
          <Route path='*' element={<NoPage />} />
        </Routes>
      );
    case 'heap': {
      const liveEnabled = user?.preferredJobsite ? isLiveEnabled(user.preferredJobsite) : true;
      return (
        <Routes key='heap-routes'>
          <Route path='/' element={<Navigate to={liveEnabled ? 'heap-live' : 'heap-recordings'} />} />
          <Route path='/documentation' element={<Navigate to={liveEnabled ? '/heap-live' : '/heap-recordings'} />} />
          <Route path='/reports' element={<Navigate to='/heap-recordings' />} />
          {liveEnabled && (
            <Route path='heap-live' element={<AuthGuarded requireJobsite={true} Component={HeapPage} />} />
          )}
          <Route
            path='heap-recordings'
            element={<AuthGuarded requireJobsite={true} Component={HeapRecordingsPage} />}
          />
          <Route path='jobsite-select' element={<AuthGuarded Component={JobsiteSelect} />} />
          <Route path='user-admin' element={<AuthGuarded Component={UserAdminPage} />} />
          <Route path='*' element={<NoPage />} />
        </Routes>
      );
    }
    case 'belt': {
      return (
        <Routes key='belt-rotues'>
          <Route path='/' element={<Navigate to='reports' />} />
          <Route path='/documentation' element={<Navigate to='/observations' />} />
          <Route path='/reports' element={<Navigate to='/observations' />} />
          <Route path='observations' element={<AuthGuarded requireJobsite={true} Component={BeltObservations} />} />
          <Route path='jobsite-select' element={<AuthGuarded Component={JobsiteSelect} />} />
          <Route path='user-admin' element={<AuthGuarded Component={UserAdminPage} />} />
          <Route path='*' element={<NoPage />} />
        </Routes>
      );
    }
    default: {
      const documentationEnabled = user?.preferredJobsite ? isDocumentationEnabled(user.preferredJobsite) : true;
      return (
        <Routes key='default-routes'>
          <Route path='/' element={<Navigate to={documentationEnabled ? 'documentation' : 'reports'} />} />
          {documentationEnabled && (
            <Route path='documentation' element={<AuthGuarded requireJobsite={true} Component={DocumentationPage} />} />
          )}
          {!documentationEnabled && <Route path='/documentation' element={<Navigate to='/reports' />} />}
          <Route path='reports' element={<AuthGuarded requireJobsite={true} Component={RecordingsPage} />} />
          <Route path='live-prediction' element={<AuthGuarded requireJobsite={true} Component={LiveViewPage} />} />
          <Route path='jobsite-select' element={<AuthGuarded Component={JobsiteSelect} />} />
          <Route path='user-admin' element={<AuthGuarded Component={UserAdminPage} />} />
          <Route path='billable-materials' element={<AuthGuarded Component={BillableMaterialPage} />} />
          <Route path='*' element={<NoPage />} />
        </Routes>
      );
    }
  }
});

export function AppRouter(): ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        {/* Non-authenticated routes */}
        <Route path='self-service/:jobsiteId/session-request' element={<SessionRequest />} />
        <Route path='self-service/:jobsiteId/:sessionId' element={<SelfServicePage />} />
        <Route path='self-service/:jobsiteId/:sessionId/infopoint' element={<SessionInfopoint />} />
        <Route path='self-service/:jobsiteId/external-api-error' element={<SessionErrorScreen />} />

        {/* All authenticated routes */}
        <Route path='/*' element={<AuthenticatedRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}
